import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../helpers/axiosInstance";
import md5 from "md5";
import { toast } from "react-toastify";

const secretKey = process.env.REACT_APP_AUTH_KEY;

// Helper: Base64 to ArrayBuffer
function base64ToArrayBuffer(base64) {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

// AES-GCM Decrypt Function
export async function decryptResponse(encryptedBase64) {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secretKey);

    const key = await window.crypto.subtle.importKey(
        "raw",
        keyData,
        { name: "AES-GCM" },
        false,
        ["decrypt"]
    );

    const encryptedData = base64ToArrayBuffer(encryptedBase64);

    const iv = encryptedData.slice(0, 12);
    const ciphertext = encryptedData.slice(12);

    const decryptedBuffer = await window.crypto.subtle.decrypt(
        {
            name: "AES-GCM",
            iv: iv
        },
        key,
        ciphertext
    );

    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
}

const initialState = {
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
};

export const postLoginUser = createAsyncThunk(
    "user/login",
    async (loginData) => {
        try {
            const url = `/user/login`;
            const response = await axiosInstance.post(url, { ...loginData, password: md5(loginData.password) });

            const decrypted = await decryptResponse(response?.data);
            let decryptedObject;
            try {
                decryptedObject = JSON.parse(decrypted);
            } catch (parseError) {
                console.error("Failed to parse decrypted data as JSON");
                decryptedObject = { rawData: decrypted }; // Fallback
            }

            // console.log("Decrypted Response:", decryptedObject);

            if (response.status === 200) {
                sessionStorage.setItem("token", JSON.stringify(response?.headers?.token));
                sessionStorage.setItem("userId", response?.headers?.userid);
                sessionStorage.setItem("uuid", response?.headers?.uuid);
                // sessionStorage.setItem("superAdmin", response?.data?.superAdmin);
            }

            return { ...decryptedObject, status: response.status };
        } catch (error) {
            if (error.response) {
                toast.error(error.response.data.message || 'Something went wrong.');
                throw error.response;
            }

            if (error.request) {
                toast.error('ERR_CONNECTION_REFUSED');
            } else {
                toast.error('An unexpected error occurred.');
            }

            throw error.response;
        }
    }
);

export const getLogoutUser = createAsyncThunk(
    "user/logout",
    async () => {
        try {
            const url = `/user/logout`;
            const response = await axiosInstance.get(url);

            return response.data;
        } catch (error) {
            toast.error(error.response.data.message || 'Something went wrong.');
            throw error.response;
        } finally {
            console.clear();
            localStorage.clear();
            sessionStorage.clear();
            // window.location.href = '/login';
            // window.location.reload();
            // persistor.purge();
        }
    }
);

export const getResendOTP = createAsyncThunk(
    "user/resend-otp",
    async ({ user, type }) => {
        try {
            const { url } = user?.routesConfig;

            const base = url?.backoffice;
            const endpointpath = 'user/resendOtp/';

            const apiURL = base + endpointpath + `${user.userId}/${type}`;

            const response = await axiosInstance.get(apiURL);
            toast.success(`New OTP sent to your ${type}`);

            return { ...response?.data, status: response.status };
        } catch (error) {
            toast.error(error.response.data.message || 'Something went wrong.');
            throw error.response;
        }
    }
);

export const postVerifyOTP = createAsyncThunk(
    "user/verify-otp",
    async ({ user, body }) => {
        try {
            const { url } = user?.routesConfig;
            const updatedBody = {
                otp: md5(body?.otp),
                userId: user?.userId,
            };

            const base = url?.backoffice;
            const endpointpath = 'user/verifyOtp';

            const apiURL = base + endpointpath;

            const response = await axiosInstance.post(apiURL, updatedBody);
            toast.success('Your are now verified!');

            if (response.status === 200) {
                sessionStorage.setItem("token", JSON.stringify(response?.headers?.token));
                sessionStorage.setItem("userId", response?.headers?.userid);
                sessionStorage.setItem("uuid", response?.headers?.uuid);
                // sessionStorage.setItem("superAdmin", response?.data?.superAdmin);
            }

            return { ...response?.data, status: response.status };
        } catch (error) {
            if (error.response) {
                toast.error(error.response.data.message || 'Something went wrong.');
                throw error.response;
            }

            if (error.request) {
                toast.error('ERR_CONNECTION_REFUSED');
            } else {
                toast.error('An unexpected error occurred.');
            }

            throw error.response;
        }
    }
);

export const putChangeUserPassword = createAsyncThunk(
    "user/change-password",
    async ({ user, body }, { dispatch }) => {
        try {
            const { url, endpoint } = user?.routesConfig;
            const updatedBody = {
                password: md5(body.password),
                newPassword: md5(body.newPassword),
                confirmPassword: md5(body.confirmPassword),
            };

            const base = url?.backoffice;
            const endpointpath = endpoint?.backoffice?.changeUserPassword;

            const apiURL = base + endpointpath;

            const response = await axiosInstance.put(apiURL, updatedBody);

            toast.success('Password changed successfully Please log in again using your new password');

            if (response?.status === 200 || response?.status === 201) {
                setTimeout(() => {
                    dispatch(getLogoutUser());
                }, 6000);
            }

            return response.data;
        } catch (error) {
            toast.error(error.response.data.message || 'Something went wrong.');
            throw error.response;
        }
    }
);

const loginUserSlice = createSlice({
    name: "loginUser",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // LOGIN
            .addCase(postLoginUser.pending, (state) => {
                state.loading = true;
                state.isAuthenticated = false;
                state.error = null;
            })
            .addCase(postLoginUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action?.payload;
                state.isAuthenticated = !action?.payload?.is2FAEnabled;
            })
            .addCase(postLoginUser.rejected, (state, action) => {
                state.loading = false;
                state.isAuthenticated = false;
                state.error = action.error.message;
            })

            // VERIFY OTP
            .addCase(postVerifyOTP.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(postVerifyOTP.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action?.payload;
                // state.isAuthenticated = !action?.payload?.is2FAEnabled;
                state.isAuthenticated = true;
            })
            .addCase(postVerifyOTP.rejected, (state, action) => {
                state.loading = false;
                state.isAuthenticated = false;
                state.error = action.error.message;
            })

            // RESEND OTP
            .addCase(getResendOTP.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getResendOTP.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(getResendOTP.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })

            // LOGOUT
            .addCase(getLogoutUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getLogoutUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = null;
                state.isAuthenticated = false;
            })
            .addCase(getLogoutUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            });
    },
});

export default loginUserSlice.reducer;